// src/components/settings/shared/EmailSettingsSection.jsx
import React, { useState } from 'react';
import { 
  Mail, 
  Bell, 
  Clock, 
  Users, 
  Palette, 
  Eye, 
  Send, 
  Edit3,
  Settings,
  CheckCircle,
  AlertTriangle 
} from 'lucide-react';
import { emailService } from '../../../services/emailService';

export const EmailSettingsSection = ({ 
  formData, 
  onInputChange, 
  bookId, 
  templateType,
  user 
}) => {
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmailError, setTestEmailError] = useState('');

  const emailTemplateOptions = [
    { value: 'welcome', label: 'Welcome Email', description: 'Sent when users first interact with your book' },
    { value: 'booking_confirmation', label: 'Booking Confirmation', description: 'Sent after successful bookings' },
    { value: 'reminder', label: 'Event Reminders', description: 'Automated reminders before events' },
    { value: 'completion_certificate', label: 'Completion Certificate', description: 'Sent when users complete your content' },
    { value: 'interaction_summary', label: 'Activity Summary', description: 'Periodic summaries of user activity' }
  ];

  const notificationFrequencies = [
    { value: 'disabled', label: 'Disabled' },
    { value: 'instant', label: 'Instant' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Summary' },
    { value: 'monthly', label: 'Monthly Report' }
  ];

  const handleSendTestEmail = async (templateType) => {
    if (!user?.email) {
      setTestEmailError('No email address available');
      return;
    }

    setSendingTest(true);
    setTestEmailError('');

    try {
      await emailService.sendCustomNotification(
        bookId,
        user.email,
        user.displayName || 'Test User',
        {
          title: `Test Email - ${templateType}`,
          message: 'This is a test email to preview how your branded emails will look to users.',
          actionButton: {
            text: 'View Your Book',
            url: `https://${formData.customDomain || 'parrotbooks.com'}`
          }
        },
        {
          primaryColor: formData.primaryColor,
          logoUrl: formData.logoUrl,
          companyName: formData.botName,
          supportEmail: formData.email,
          website: formData.website
        }
      );
      
      setTestEmailSent(true);
      setTimeout(() => setTestEmailSent(false), 3000);
    } catch (error) {
      setTestEmailError('Failed to send test email: ' + error.message);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Mail className="h-7 w-7 text-blue-600" />
          Email Notifications
        </h3>
        <p className="text-gray-600">
          Configure automated emails and customize branding for user communications.
        </p>
      </div>

      {/* Email Templates */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-600" />
          Email Templates
        </h4>
        
        <div className="space-y-4">
          {emailTemplateOptions.map(template => (
            <div key={template.value} className="flex items-center justify-between p-4 bg-white rounded-lg border">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData[`${template.value}Enabled`] || false}
                    onChange={(e) => onInputChange(`${template.value}Enabled`, e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <h5 className="font-medium text-gray-900">{template.label}</h5>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </div>
              </div>
              
              {formData[`${template.value}Enabled`] && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSendTestEmail(template.value)}
                    disabled={sendingTest}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                  >
                    {sendingTest ? 'Sending...' : 'Test'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {testEmailSent && (
          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-green-800 text-sm">Test email sent successfully!</span>
          </div>
        )}

        {testEmailError && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-red-800 text-sm">{testEmailError}</span>
          </div>
        )}
      </div>

      {/* Notification Frequency */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-600" />
          Notification Frequency
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Notifications
            </label>
            <select
              value={formData.userNotificationFreq || 'weekly'}
              onChange={(e) => onInputChange('userNotificationFreq', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {notificationFrequencies.map(freq => (
                <option key={freq.value} value={freq.value}>{freq.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Notifications
            </label>
            <select
              value={formData.adminNotificationFreq || 'daily'}
              onChange={(e) => onInputChange('adminNotificationFreq', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {notificationFrequencies.map(freq => (
                <option key={freq.value} value={freq.value}>{freq.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Email Branding Preview */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Palette className="h-5 w-5 text-purple-600" />
          Email Branding Preview
        </h4>
        
        <div className="bg-gray-100 p-6 rounded-lg">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Email Header Preview */}
            <div 
              className="p-6 text-center text-white"
              style={{ backgroundColor: formData.primaryColor || '#0795ff' }}
            >
              {formData.logoUrl && (
                <img 
                  src={formData.logoUrl} 
                  alt="Logo" 
                  className="max-h-12 mx-auto mb-3"
                />
              )}
              <h3 className="text-lg font-bold">Welcome!</h3>
              <p className="text-sm opacity-90">Email preview</p>
            </div>
            
            {/* Email Content Preview */}
            <div className="p-6">
              <p className="text-gray-700 text-sm mb-4">
                Hi there,<br/><br/>
                This is how your branded emails will appear to users.
              </p>
              <div className="text-center">
                <button 
                  className="px-4 py-2 text-white rounded text-sm font-medium"
                  style={{ backgroundColor: formData.primaryColor || '#0795ff' }}
                >
                  Call to Action
                </button>
              </div>
            </div>
            
            {/* Email Footer Preview */}
            <div className="bg-gray-50 p-4 text-center">
              <p className="text-xs text-gray-600">
                © {formData.botName || 'Your Book Name'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Message Templates */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Edit3 className="h-5 w-5 text-green-600" />
          Custom Messages
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Welcome Email Message
            </label>
            <textarea
              value={formData.welcomeMessage || ''}
              onChange={(e) => onInputChange('welcomeMessage', e.target.value)}
              placeholder="Enter a custom welcome message for new users..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Completion Message
            </label>
            <textarea
              value={formData.completionMessage || ''}
              onChange={(e) => onInputChange('completionMessage', e.target.value)}
              placeholder="Message to show when users complete your content..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};